/**
 * Runtime validation for the IR.
 *
 * The type language above is the compile-time contract; this is the
 * runtime one. It matters at exactly two boundaries: a compiler's output
 * (a bad node must fail before it reaches a signed document) and the API
 * (a consumer — Hyran, or any client authoring its own blocks — posts IR
 * we did not produce).
 *
 * Validation is deliberately structural and closed (`strict`): an unknown
 * node type or a stray styling property is an error, not a passthrough.
 * That is what stops the IR from silently drifting into "arbitrary JSON".
 */
import { z } from 'zod';
import { IR_SCHEMA, IR_VERSION } from './nodes.js';
const valueFormat = z.enum(['text', 'date', 'number', 'currency', 'percent', 'duration', 'legal_id', 'checkbox']);
export const inlineNodeSchema = z.lazy(() => z.discriminatedUnion('type', [
    z.object({ type: z.literal('text'), text: z.string() }).strict(),
    z.object({ type: z.literal('strong'), content: z.array(inlineNodeSchema) }).strict(),
    z.object({ type: z.literal('emphasis'), content: z.array(inlineNodeSchema) }).strict(),
    z.object({ type: z.literal('lineBreak') }).strict(),
    z.object({ type: z.literal('label'), content: z.array(inlineNodeSchema) }).strict(),
    z
        .object({
        type: z.literal('image'),
        src: z.string().min(1),
        alt: z.string().optional(),
        maxHeight: z.string().optional(),
        maxWidth: z.string().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('value'),
        text: z.string().nullable(),
        raw: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
        format: valueFormat.optional(),
        blankWidth: z.enum(['short', 'medium', 'long', 'full']).optional(),
        sourceKey: z.string().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('sectionRef'),
        target: z.string().min(1),
        display: z.enum(['number', 'title', 'numberAndTitle']).optional(),
        text: z.string().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('attachmentRef'),
        target: z.string().min(1),
        display: z.enum(['number', 'label', 'numberAndLabel']).optional(),
        text: z.string().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('documentRef'),
        target: z.string().min(1),
        display: z.literal('title').optional(),
        text: z.string().optional(),
    })
        .strict(),
]));
const layoutHints = {
    instanceId: z.string().optional(),
    locked: z.boolean().optional(),
    keepTogether: z.boolean().optional(),
    keepWithNext: z.boolean().optional(),
    pageBreakBefore: z.boolean().optional(),
    repeatTableHeader: z.boolean().optional(),
    sideBySide: z.boolean().optional(),
};
const inline = z.array(inlineNodeSchema);
export const blockNodeSchema = z.lazy(() => z.discriminatedUnion('type', [
    z
        .object({
        ...layoutHints,
        type: z.literal('section'),
        ref: z.string().min(1),
        title: z.string().optional(),
        content: z.array(blockNodeSchema),
        numbering: z.enum(['auto', 'none', 'fixed']).optional(),
        childFormat: z.enum(['digit', 'letter']).optional(),
        restartNumbering: z.boolean().optional(),
        variant: z.enum(['heading', 'clause']).optional(),
        number: z.string().nullable().optional(),
        path: z.array(z.string()).optional(),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('heading'),
        level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
        content: inline,
        align: z.enum(['left', 'center']).optional(),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('paragraph'),
        content: inline,
        variant: z.enum(['body', 'lead', 'fine_print', 'caption', 'preamble', 'intro']).optional(),
        align: z.enum(['left', 'center', 'right']).optional(),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('list'),
        ordered: z.boolean().optional(),
        items: z.array(z.object({ content: z.array(blockNodeSchema) }).strict()),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('optionList'),
        select: z.enum(['single', 'multi']).optional(),
        layout: z.enum(['stack', 'inline']).optional(),
        sourceKey: z.string().min(1).optional(),
        items: z.array(z
            .object({
            checked: z.boolean(),
            content: inline,
            children: z.array(blockNodeSchema).optional(),
            code: z.string().optional(),
            sourceKey: z.string().min(1).optional(),
        })
            .strict()),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('table'),
        columns: z.array(z
            .object({
            key: z.string().min(1),
            header: inline.optional(),
            align: z.enum(['left', 'center', 'right']).optional(),
            width: z.number().min(1).max(100).optional(),
        })
            .strict()),
        rows: z.array(z
            .object({
            cells: z.array(z.object({ content: z.array(blockNodeSchema), colSpan: z.number().int().min(1).optional() }).strict()),
            variant: z.enum(['body', 'total']).optional(),
        })
            .strict()),
        caption: inline.optional(),
        variant: z.enum(['data', 'details', 'plain']).optional(),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('detailList'),
        items: z.array(z.object({ term: inline, description: z.array(blockNodeSchema) }).strict()),
        variant: z.enum(['inline', 'boxed']).optional(),
        columns: z.union([z.literal(1), z.literal(2)]).optional(),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('signature'),
        statement: inline.optional(),
        columns: z.union([z.literal(1), z.literal(2)]).optional(),
        heading: z.string().optional(),
        panels: z.array(z
            .object({
            label: z.string(),
            partyRef: z.string().optional(),
            name: z.string().nullable().optional(),
            entity: z.string().nullable().optional(),
            capacity: z.string().nullable().optional(),
            legalId: z.string().nullable().optional(),
            showPlaceDate: z.boolean().optional(),
            anchor: z.string().optional(),
            role: z.string().optional(),
            ordinal: z.number().int().min(0).optional(),
            signerIndex: z.number().int().min(0).optional(),
            signerCount: z.number().int().min(1).optional(),
        })
            .strict()),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('attachmentList'),
        entries: z
            .array(z.object({ id: z.string(), number: z.number().nullable(), label: z.string() }).strict())
            .optional(),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('notice'),
        tone: z.enum(['info', 'warning', 'legal']).optional(),
        title: z.string().optional(),
        content: z.array(blockNodeSchema),
    })
        .strict(),
    z
        .object({
        ...layoutHints,
        type: z.literal('group'),
        variant: z.enum(['plain', 'boxed', 'indent', 'columns']).optional(),
        title: z.string().optional(),
        content: z.array(blockNodeSchema),
    })
        .strict(),
    z.object({ type: z.literal('spacer'), size: z.enum(['sm', 'md', 'lg']).optional(), instanceId: z.string().optional(), locked: z.boolean().optional() }).strict(),
    z.object({ type: z.literal('divider'), instanceId: z.string().optional(), locked: z.boolean().optional() }).strict(),
    z.object({ type: z.literal('pageBreak'), instanceId: z.string().optional(), locked: z.boolean().optional() }).strict(),
]));
export const attachmentDeclSchema = z
    .object({
    id: z.string().min(1),
    label: z.string(),
    fixedNumber: z.number().int().min(1).nullable().optional(),
    pending: z.boolean().optional(),
    number: z.number().int().nullable().optional(),
})
    .strict();
export const documentPlaneSchema = z.enum(['legal', 'shared', 'tenant']);
export const documentPartSchema = z
    .object({
    key: z.string().min(1),
    title: z.string(),
    letterhead: z.array(blockNodeSchema).optional(),
    body: z.array(blockNodeSchema),
    footer: z.array(blockNodeSchema).optional(),
})
    .strict();
export const documentIrSchema = z
    .object({
    schema: z.literal(IR_SCHEMA),
    version: z.literal(IR_VERSION),
    locale: z.string().min(2),
    title: z.string(),
    numberStyle: z.enum(['paragraph', 'point', 'plain']).optional(),
    parts: z.array(documentPartSchema).min(1),
    attachments: z.array(attachmentDeclSchema),
    manifest: z.array(z
        .object({
        instanceId: z.string(),
        blockKey: z.string(),
        blockVersion: z.string(),
        blockMinor: z.number().int().min(0).optional(),
        plane: documentPlaneSchema,
    })
        .strict()),
    resolved: z.literal(true).optional(),
    outline: z
        .array(z
        .object({
        ref: z.string(),
        number: z.string().nullable(),
        title: z.string().optional(),
        part: z.string(),
        instanceId: z.string().optional(),
    })
        .strict())
        .optional(),
})
    .strict();
/** Validates a document IR. Returns issues rather than throwing. */
export function validateDocumentIR(input) {
    const parsed = documentIrSchema.safeParse(input);
    if (parsed.success)
        return { ok: true, value: parsed.data };
    return {
        ok: false,
        issues: parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })),
    };
}
/** Validates a single block subtree — used on compiler output. */
export function validateBlockNodes(input) {
    const parsed = z.array(blockNodeSchema).safeParse(input);
    if (parsed.success)
        return { ok: true };
    return { ok: false, issues: parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })) };
}
//# sourceMappingURL=schema.js.map